import type { CustomLayerInterface } from 'mapbox-gl';
import { decomposeColor } from '@mui/material/styles';
import {
  compileShader,
  linkProgram,
} from '@features/design/threejs/gl-program.helpers';

export const PROJECT_MAP_GRADIENT_LAYER_ID = 'project-map-gradient';
export const PROJECT_MAP_FOREGROUND_GRADIENT_LAYER_ID =
  'project-map-foreground-gradient';

export const PROJECT_MAP_VERTICAL_FOREGROUND_GRADIENT_LAYER_ID =
  'project-map-vertical-foreground-gradient';

/** Screen-space tint that leaves project feature hit testing unchanged. */
export const createProjectMapGradientLayer = (
  color: string,
  isVisible: () => boolean,
  {
    id = PROJECT_MAP_GRADIENT_LAYER_ID,
    opacity = 1,
    endOpacity = 0.1,
    direction = 'right',
    opacityStops,
    stopPositions = [0, 0.25, 0.5, 0.75, 1],
  }: {
    id?: string;
    opacity?: number | (() => number);
    endOpacity?: number | (() => number);
    direction?: 'right' | 'down';
    opacityStops?: readonly [number, number, number, number, number];
    stopPositions?: readonly [number, number, number, number, number];
  } = {},
): CustomLayerInterface => {
  const rgb = decomposeColor(color)
    .values.slice(0, 3)
    .map((value) => value / 255);
  let program: WebGLProgram | undefined;
  let buffer: WebGLBuffer | null = null;
  let vao: WebGLVertexArrayObject | null = null;
  let colorUniform: WebGLUniformLocation | null = null;
  let opacityUniform: WebGLUniformLocation | null = null;
  let endOpacityUniform: WebGLUniformLocation | null = null;
  return {
    id,
    type: 'custom',
    slot: 'top',
    renderingMode: '2d',
    onAdd: (_map, gl) => {
      const vertex = compileShader(
        gl,
        gl.VERTEX_SHADER,
        `#version 300 es
        in vec2 a_position;
        out float v_progress;
        void main() {
          v_progress = ${direction === 'down' ? '(1.0 - a_position.y)' : '(a_position.x + 1.0)'} * 0.5;
          gl_Position = vec4(a_position, 0.0, 1.0);
        }
      `,
      );
      const fragment = compileShader(
        gl,
        gl.FRAGMENT_SHADER,
        `#version 300 es
        precision mediump float;
        in float v_progress;
        uniform vec3 u_color;
        uniform float u_opacity;
        uniform float u_end_opacity;
        out vec4 out_color;
        void main() {
          float opacity = (1.0 - (1.0 - u_end_opacity) * v_progress * v_progress) * u_opacity;
          ${
            opacityStops
              ? `float stops[5] = float[5](${opacityStops.map((stop) => stop.toFixed(6)).join(', ')});
          float positions[5] = float[5](${stopPositions.map((position) => position.toFixed(6)).join(', ')});
          int index = 0;
          for (int i = 1; i < 4; i++) {
            if (v_progress >= positions[i]) index = i;
          }
          float fraction = clamp((v_progress - positions[index]) / (positions[index + 1] - positions[index]), 0.0, 1.0);
          opacity = mix(stops[index], stops[index + 1], fraction) * u_opacity;`
              : ''
          }
          out_color = vec4(u_color * opacity, opacity);
        }
      `,
      );
      program = linkProgram(gl, vertex, fragment);
      colorUniform = gl.getUniformLocation(program, 'u_color');
      opacityUniform = gl.getUniformLocation(program, 'u_opacity');
      endOpacityUniform = gl.getUniformLocation(program, 'u_end_opacity');
      buffer = gl.createBuffer();
      vao = gl.createVertexArray();
      const previousVao = gl.getParameter(gl.VERTEX_ARRAY_BINDING);
      gl.bindVertexArray(vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
        gl.STATIC_DRAW,
      );
      const position = gl.getAttribLocation(program, 'a_position');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      gl.bindVertexArray(previousVao);
    },
    render: (gl) => {
      if (!isVisible() || !program || !buffer || !vao) return;
      const layerOpacity = typeof opacity === 'function' ? opacity() : opacity;
      if (layerOpacity <= 0) return;
      gl.useProgram(program);
      gl.bindVertexArray(vao);
      gl.uniform3fv(colorUniform, rgb);
      gl.uniform1f(opacityUniform, layerOpacity);
      gl.uniform1f(
        endOpacityUniform,
        typeof endOpacity === 'function' ? endOpacity() : endOpacity,
      );
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.STENCIL_TEST);
      gl.disable(gl.CULL_FACE);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      gl.bindVertexArray(null);
    },
    onRemove: (_map, gl) => {
      if (program) gl.deleteProgram(program);
      if (buffer) gl.deleteBuffer(buffer);
      if (vao) gl.deleteVertexArray(vao);
      program = undefined;
      buffer = null;
      vao = null;
    },
  };
};
